// The extension logs LinkedIn image messages as an "Image attached" line
// followed by the LinkedIn image url. The url needs the viewer LinkedIn
// session, so it is loaded through the extension and the label is shown when
// that fails.

export const LINKEDIN_IMAGE_LABEL = 'Image attached';

const LINKEDIN_IMAGE_URL = /^https:\/\/www\.linkedin\.com\/dms\/\S+$/;

function isLinkedinImageUrl(candidate: string): boolean {
  if (!LINKEDIN_IMAGE_URL.test(candidate)) {
    return false;
  }
  try {
    const url = new URL(candidate);
    return (
      url.origin === 'https://www.linkedin.com' &&
      url.pathname.startsWith('/dms/prv/')
    );
  } catch {
    return false;
  }
}

export type LinkedinMessageSegment =
  | { type: 'text'; text: string }
  | { type: 'image'; url: string };

export function parseLinkedinMessageContent(
  content: string | null | undefined,
): LinkedinMessageSegment[] {
  if (!content) {
    return [];
  }

  const segments: LinkedinMessageSegment[] = [];
  let textLines: string[] = [];

  const flushText = () => {
    if (textLines.length > 0) {
      segments.push({ type: 'text', text: textLines.join('\n') });
      textLines = [];
    }
  };

  const lines = content.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const nextLine = lines[i + 1]?.trim();
    if (
      lines[i].trim() === LINKEDIN_IMAGE_LABEL &&
      nextLine &&
      isLinkedinImageUrl(nextLine)
    ) {
      flushText();
      segments.push({ type: 'image', url: nextLine });
      i++;
      continue;
    }
    textLines.push(lines[i]);
  }
  flushText();

  return segments;
}

// Text-only version for previews: images become their label
export function linkedinMessagePreviewText(
  content: string | null | undefined,
): string {
  return parseLinkedinMessageContent(content)
    .map((segment) =>
      segment.type === 'image' ? LINKEDIN_IMAGE_LABEL : segment.text,
    )
    .join('\n');
}

// Drops the labels of media that the caller already renders on its own
// (voice player, attachment cards), so it is not shown twice
export function withoutRenderedMediaLabels(
  content: string,
  rendered: { voiceMessage: boolean; fileNames: string[] },
): string {
  const renderedLabels = new Set<string>(
    rendered.fileNames.map((name) => `File attached: ${name}`),
  );
  if (rendered.fileNames.length > 0) {
    renderedLabels.add('File attached');
  }
  if (rendered.voiceMessage) {
    renderedLabels.add('Voice message attached');
  }
  return content
    .split('\n')
    .filter((line) => !renderedLabels.has(line.trim()))
    .join('\n');
}

const LIVE_MATCH_WINDOW_MS = 2 * 60 * 1000;

type LiveLinkedinMessage = {
  message: string;
  timestamp: number;
  sent: boolean;
  attachments?: { url: string; mediaType: string }[];
};

// Images of a logged LinkedIn message taken from the live LinkedIn
// conversation, for messages logged before their media was saved in Crono
export function findLiveLinkedinMessageImages(
  logged: {
    content: string | null | undefined;
    eventDatetime: Date | string;
    sent: boolean;
  },
  liveMessages: LiveLinkedinMessage[],
): string[] {
  const loggedText = (logged.content ?? '').trim();
  const loggedTime = new Date(logged.eventDatetime).getTime();

  const candidates = liveMessages.filter((live) => {
    if (live.sent !== logged.sent) {
      return false;
    }
    if (Math.abs(live.timestamp - loggedTime) > LIVE_MATCH_WINDOW_MS) {
      return false;
    }
    // Newer extension versions append the media lines to the message text
    const liveText = live.message.trim();
    return (
      liveText === loggedText ||
      (loggedText !== '' && liveText.startsWith(`${loggedText}\n`)) ||
      (loggedText === '' &&
        parseLinkedinMessageContent(liveText).every(
          (segment) => segment.type === 'image',
        ))
    );
  });

  const match = candidates.sort(
    (a, b) =>
      Math.abs(a.timestamp - loggedTime) - Math.abs(b.timestamp - loggedTime),
  )[0];
  if (!match) {
    return [];
  }

  const urls = [
    ...parseLinkedinMessageContent(match.message).flatMap((segment) =>
      segment.type === 'image' ? [segment.url] : [],
    ),
    ...(match.attachments ?? [])
      .filter(
        (attachment) =>
          attachment.mediaType === 'image' ||
          attachment.mediaType.startsWith('image/'),
      )
      .map((attachment) => attachment.url),
  ];
  return [...new Set(urls)];
}
